import { useEffect, useState } from "react";
import { ClipboardList, Lock, Loader2, Trash2, Plus, Users, GripVertical, Settings2, Flag } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { RaiseModal } from "@/components/RaiseToAdmin";
import { useAuth } from "@/context/AuthContext";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);
const inp = "bg-navy-950 border border-gold-500/25 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-gold-500/60 w-full";
const LEVELS = [["administrator", "Administrator — manage boards + all tasks"], ["manager", "Manager — all tasks, no board admin"], ["member", "Member — their own board only"]];

function FlagBadge({ t }) {
  const [img, setImg] = useState(null);
  const [showImg, setShowImg] = useState(false);
  if (!t.flag) return null;
  const view = async () => {
    if (!img) {
      try { const { data } = await api.get(`/tasks/${t.id}/flag_image`); setImg(data.image_b64); } catch { return; }
    }
    setShowImg((s) => !s);
  };
  return (
    <div className="mt-2 border border-red-400/30 bg-red-500/5 px-3 py-2" data-testid={`task-flag-${t.id}`}>
      <Mono className="text-[0.5rem] text-red-300 inline-flex items-center gap-1.5"><Flag className="h-3 w-3" /> {t.flag.by} · {t.flag.at?.slice(0, 10)}</Mono>
      <p className="text-white/70 text-xs font-light mt-1">"{t.flag.note}"</p>
      {t.flag.has_image && (
        <button onClick={view} className="text-red-300/80 hover:text-red-200 text-[0.65rem] mt-1.5" data-testid={`task-flag-img-btn-${t.id}`}>
          {showImg ? "Hide screenshot" : "View screenshot"}
        </button>
      )}
      {showImg && img && <img src={img} alt="flag screenshot" className="mt-2 max-w-full border border-red-400/30" data-testid={`task-flag-img-${t.id}`} />}
    </div>
  );
}

function TaskCard({ t, canEdit, onDelete, onMove, onFlagged }) {
  const [open, setOpen] = useState(t.priority === "high" && t.status === "todo");
  const [flagging, setFlagging] = useState(false);
  return (
    <div draggable={canEdit} onDragStart={(e) => e.dataTransfer.setData("text/task", t.id)}
      data-testid={`task-card-${t.id}`}
      className={`border bg-navy-900/50 p-4 select-none ${canEdit ? "cursor-grab active:cursor-grabbing" : ""} ${t.status === "done" ? "border-emerald-400/30 opacity-75" : t.priority === "high" ? "border-gold-500/50" : "border-gold-500/20"}`}>
      <div className="flex items-start gap-2.5">
        <GripVertical className="h-4 w-4 text-white/20 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setOpen((o) => !o)} className="text-left">
              <span className={`text-sm ${t.status === "done" ? "text-white/55 line-through decoration-white/25" : "text-white"}`}>{t.title}</span>
            </button>
            {t.priority === "high" && t.status !== "done" && <Mono className="text-[0.5rem] text-gold-300 border border-gold-500/40 px-1.5 py-0.5">Priority</Mono>}
          </div>
          {open && t.description && (
            <p className="text-white/55 font-light text-xs leading-relaxed mt-2 whitespace-pre-wrap" data-testid={`task-desc-${t.id}`}>{t.description}</p>
          )}
          <div className="flex items-center gap-3 mt-2">
            {t.description && <button onClick={() => setOpen((o) => !o)} className="text-gold-400/70 hover:text-gold-300 text-[0.65rem]">{open ? "Hide detail" : "Show detail"}</button>}
            {canEdit && (
              <button onClick={() => onMove(t.id, t.status === "done" ? "todo" : "done")} data-testid={`task-toggle-${t.id}`}
                className={`text-[0.65rem] ${t.status === "done" ? "text-white/40 hover:text-gold-300" : "text-emerald-300/80 hover:text-emerald-300"}`}>
                {t.status === "done" ? "Reopen" : "Mark done"}
              </button>
            )}
            {t.completed_at && <Mono className="text-[0.5rem] text-emerald-300/70">done {t.completed_at.slice(0, 10)}</Mono>}
            {canEdit && t.status !== "done" && !t.flag && (
              <button onClick={() => setFlagging(true)} data-testid={`task-flag-btn-${t.id}`}
                className="text-red-300/60 hover:text-red-300 text-[0.65rem] inline-flex items-center gap-1">
                <Flag className="h-3 w-3" /> Stuck? Raise it
              </button>
            )}
          </div>
          <FlagBadge t={t} />
        </div>
        {canEdit && <button onClick={onDelete} data-testid={`task-del-${t.id}`} className="text-white/20 hover:text-red-400 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>}
      </div>
      {flagging && <RaiseModal contextTitle={t.title} onClose={() => setFlagging(false)} onDone={() => { setFlagging(false); onFlagged?.(); }}
        submit={(note, image) => api.post(`/tasks/${t.id}/flag`, { note, image_b64: image })} />}
    </div>
  );
}

function Column({ title, hint, status, tasks, canEdit, onDropTask, onDelete, onMove, onFlagged, accent, testid }) {
  const [over, setOver] = useState(false);
  return (
    <div data-testid={testid}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const id = e.dataTransfer.getData("text/task"); if (id && canEdit) onDropTask(id, status); }}
      className={`flex-1 min-w-[300px] border p-4 space-y-3 transition-colors ${over ? "border-gold-500 bg-gold-500/5" : accent}`}>
      <div>
        <Mono className="text-[0.6rem] text-gold-400">{title} ({tasks.length})</Mono>
        <p className="text-white/35 text-[0.65rem] font-light mt-1">{hint}</p>
      </div>
      {tasks.length === 0 ? (
        <div className="border border-dashed border-white/10 p-8 text-center text-white/30 text-xs font-light">
          {status === "done" ? "Drag completed tasks here." : "All clear."}
        </div>
      ) : (
        tasks.map((t) => <TaskCard key={t.id} t={t} canEdit={canEdit} onDelete={() => onDelete(t.id)} onMove={onMove} onFlagged={onFlagged} />)
      )}
    </div>
  );
}

function PanelStrip() {
  const [data, setData] = useState(null);
  const load = () => api.get("/panels").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const colors = { not_started: "border-white/15 text-white/40", preparing: "border-gold-500/50 text-gold-300", lodged: "border-sky-400/50 text-sky-300", approved: "border-emerald-400/50 text-emerald-300" };
  return (
    <div className="border border-gold-500/20 bg-navy-900/40 p-4" data-testid="panel-tracker">
      <Mono className="text-[0.6rem] text-gold-400">Panel tracker · procurement coverage</Mono>
      <div className="flex gap-3 flex-wrap mt-3">
        {data.panels.map((p) => (
          <div key={p.id} className={`border px-3 py-2.5 min-w-[210px] ${colors[p.status]}`} data-testid={`panel-${p.id}`}>
            <div className="text-white text-xs">{p.name}</div>
            <div className="text-white/35 text-[0.6rem] font-light">{p.jurisdiction}</div>
            {data.can_edit ? (
              <select value={p.status} data-testid={`panel-status-${p.id}`}
                onChange={async (e) => { await api.patch(`/panels/${p.id}`, { status: e.target.value }); load(); }}
                className="bg-navy-950 border border-gold-500/25 rounded-sm px-2 py-1 text-[0.65rem] mt-2 w-full">
                {data.statuses.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </select>
            ) : (
              <Mono className="text-[0.5rem] block mt-2">{p.status.replace("_", " ")}</Mono>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function BoardManager({ boards, onChanged }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", member_email: "", access_level: "member" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api.post("/tasks/boards", form);
      setForm({ name: "", member_email: "", access_level: "member" });
      onChanged();
    } catch (ex) { setErr(ex?.response?.data?.detail || "Couldn't create the board."); }
    finally { setBusy(false); }
  };
  const setLevel = async (bid, access_level) => { await api.patch(`/tasks/boards/${bid}`, { access_level }); onChanged(); };
  const setEmail = async (bid, member_email) => { await api.patch(`/tasks/boards/${bid}`, { member_email }); onChanged(); };
  const remove = async (bid) => {
    if (!window.confirm("Delete this board and every task on it?")) return;
    await api.delete(`/tasks/boards/${bid}`); onChanged();
  };

  return (
    <div className="border border-gold-500/25 bg-navy-900/40" data-testid="board-manager">
      <button onClick={() => setOpen((o) => !o)} data-testid="board-manager-toggle"
        className="w-full flex items-center gap-2 px-4 py-3 text-left text-gold-300 text-sm">
        <Settings2 className="h-4 w-4" /> Board profiles &amp; administration levels
        <span className="text-white/35 text-xs font-light ml-2">{boards.length} board{boards.length === 1 ? "" : "s"}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-4">
          <div className="space-y-2">
            {boards.map((b) => (
              <div key={b.id} className="flex items-center gap-3 flex-wrap border border-white/10 p-3" data-testid={`board-row-${b.id}`}>
                <Users className="h-3.5 w-3.5 text-gold-400/60 shrink-0" />
                <span className="text-white text-sm min-w-[140px]">{b.name}</span>
                <input defaultValue={b.member_email || ""} placeholder="sign-in email (none = unassigned)"
                  onBlur={(e) => e.target.value !== (b.member_email || "") && setEmail(b.id, e.target.value)}
                  data-testid={`board-email-${b.id}`}
                  className="bg-navy-950 border border-gold-500/20 rounded-sm px-2 py-1.5 text-xs flex-1 min-w-[200px]" />
                <select value={b.access_level} onChange={(e) => setLevel(b.id, e.target.value)} data-testid={`board-level-${b.id}`}
                  className="bg-navy-950 border border-gold-500/20 rounded-sm px-2 py-1.5 text-xs">
                  {LEVELS.map(([v]) => <option key={v} value={v}>{v}</option>)}
                </select>
                <button onClick={() => remove(b.id)} data-testid={`board-del-${b.id}`} className="text-white/25 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
          <form onSubmit={add} className="flex items-end gap-3 flex-wrap" data-testid="board-add-form">
            <div className="flex-1 min-w-[160px]">
              <label className="font-mono uppercase tracking-[0.18em] text-white/45 text-[0.5rem] block mb-1">Board name</label>
              <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Russell · QLD Champion" data-testid="board-name-input" className={inp} />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="font-mono uppercase tracking-[0.18em] text-white/45 text-[0.5rem] block mb-1">Their sign-in email</label>
              <input value={form.member_email} onChange={(e) => setForm((f) => ({ ...f, member_email: e.target.value }))} placeholder="optional — links their login" data-testid="board-email-input" className={inp} />
            </div>
            <div>
              <label className="font-mono uppercase tracking-[0.18em] text-white/45 text-[0.5rem] block mb-1">Administration level</label>
              <select value={form.access_level} onChange={(e) => setForm((f) => ({ ...f, access_level: e.target.value }))} data-testid="board-level-input" className="bg-navy-950 border border-gold-500/25 rounded-sm px-3 py-2 text-sm">
                {LEVELS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
            </div>
            <button type="submit" disabled={busy || !form.name.trim()} data-testid="board-save-btn"
              className="bg-gold-500 text-navy-950 font-semibold px-4 py-2 text-sm hover:bg-gold-400 disabled:opacity-50 inline-flex items-center gap-2">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} <Plus className="h-4 w-4" /> Create board
            </button>
          </form>
          {err && <p className="text-amber-300 text-xs" data-testid="board-error">{err}</p>}
          <p className="text-white/35 text-[0.65rem] font-light">New team member? Create their board with their email and pick a level. When they register and sign in, they'll see exactly what you've dictated — members only ever see their own board.</p>
        </div>
      )}
    </div>
  );
}

export default function TaskBoard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [board, setBoard] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", priority: "normal" });
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = () => api.get("/tasks")
    .then((r) => { setData(r.data); setBoard((b) => b && r.data.boards.some((x) => x.id === b) ? b : r.data.boards[0]?.id || null); })
    .catch((e) => { if (e?.response?.status === 403) setDenied(true); });
  useEffect(() => { load(); }, []); // eslint-disable-line

  if (denied) {
    return (
      <div className="text-white max-w-lg mx-auto text-center py-24" data-testid="tasks-restricted">
        <Lock className="h-10 w-10 text-gold-400 mx-auto mb-5" />
        <h1 className="font-heading font-light text-3xl">No board yet</h1>
        <p className="text-white/50 font-light mt-3">Ask the administrator to create a board profile for your sign-in.</p>
      </div>
    );
  }
  if (!data) return <div className="text-white/40 text-sm" data-testid="tasks-loading">Loading task board…</div>;

  const { tasks, boards, me } = data;
  const isAdminLevel = me.level === "administrator";
  const canEditBoard = (bid) => me.level !== "member" || bid === me.board_id;

  const move = async (id, status) => {
    const t = tasks.find((x) => x.id === id);
    if (!t || t.status === status) return;
    setData((d) => ({ ...d, tasks: d.tasks.map((x) => (x.id === id ? { ...x, status, completed_at: status === "done" ? new Date().toISOString() : null } : x)) }));
    try { await api.patch(`/tasks/${id}`, { status }); } finally { load(); }
  };
  const del = async (id) => { await api.delete(`/tasks/${id}`); load(); };
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/tasks", { ...form, board });
      setForm({ title: "", description: "", priority: "normal" });
      setAdding(false);
      load();
    } finally { setBusy(false); }
  };

  const boardTasks = tasks.filter((t) => t.board === board);
  const todo = boardTasks.filter((t) => t.status === "todo");
  const done = boardTasks.filter((t) => t.status === "done");
  const editable = canEditBoard(board);

  return (
    <div className="space-y-8 text-white" data-testid="task-board-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Mono className="text-gold-400 text-[0.65rem] inline-flex items-center gap-2"><ClipboardList className="h-3 w-3" /> Operations · work queue</Mono>
          <h1 className="font-heading font-light text-4xl tracking-tight mt-3">Task Board</h1>
          <p className="text-white/55 mt-3 font-light">Work the left column top to bottom. When a task is finished to standard, drag it to the right.</p>
        </div>
        {editable && (
          <button onClick={() => setAdding((a) => !a)} data-testid="task-add-toggle"
            className="inline-flex items-center gap-2 bg-gold-500 text-navy-950 font-semibold px-4 py-2.5 text-sm hover:bg-gold-400 transition-colors">
            <Plus className="h-4 w-4" /> New task
          </button>
        )}
      </div>

      <PanelStrip />

      {isAdminLevel && <BoardManager boards={boards} onChanged={load} />}

      <div className="flex items-center gap-2 flex-wrap" data-testid="task-board-tabs">
        {boards.map((b) => (
          <button key={b.id} onClick={() => setBoard(b.id)} data-testid={`task-board-tab-${b.id}`}
            className={`border px-5 py-2.5 text-sm transition-colors ${board === b.id ? "border-gold-500 text-gold-300 bg-gold-500/10" : "border-gold-500/20 text-white/55 hover:text-gold-300"}`}>
            {b.name}
          </button>
        ))}
      </div>

      {adding && editable && (
        <form onSubmit={add} className="border border-gold-500/25 bg-navy-900/40 p-5 space-y-3 max-w-2xl" data-testid="task-add-form">
          <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Task title" data-testid="task-title-input" className={inp} />
          <textarea rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Detailed description — exactly what done-to-standard looks like, scripts, links, who to contact" data-testid="task-desc-input" className={inp} />
          <div className="flex items-center gap-3">
            <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} data-testid="task-priority-select" className="bg-navy-950 border border-gold-500/25 rounded-sm px-3 py-2 text-sm">
              <option value="normal">Normal</option>
              <option value="high">Priority</option>
            </select>
            <button type="submit" disabled={busy || !form.title.trim()} data-testid="task-save-btn"
              className="bg-gold-500 text-navy-950 font-semibold px-5 py-2 text-sm hover:bg-gold-400 disabled:opacity-50 inline-flex items-center gap-2">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Add to board
            </button>
          </div>
        </form>
      )}

      <div className="flex gap-6 flex-wrap items-start">
        <Column title="To do" hint="Detailed instructions inside each card — tap 'Show detail'." status="todo" tasks={todo} canEdit={editable}
          onDropTask={move} onDelete={del} onMove={move} onFlagged={load} accent="border-gold-500/25" testid="task-col-todo" />
        <Column title="Completed · to standard" hint="Only drag here when it's done properly — checked, recorded, no loose ends." status="done" tasks={done} canEdit={editable}
          onDropTask={move} onDelete={del} onMove={move} onFlagged={load} accent="border-emerald-400/25" testid="task-col-done" />
      </div>
    </div>
  );
}
